import { useCallback, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLocationSearch } from '#src/app/pages/search/shared/hooks';
import type { LocationItem, LocationItemTypeEnum } from '#src/app/pages/search/shared/types';

type Result = ModelHookResult<
	{
		data: {
			isLoading: boolean;
			isError: boolean;
			locations: readonly LocationItem[];
		};
		input: {
			inputRef: React.RefObject<HTMLInputElement | null>;
			searchTerm: string;
		};
	},
	{
		handleInputFocus: () => void;
		handleInputChange: (value: string) => void;
		clearSearch: () => void;
		selectLocation: (value: string, type: LocationItemTypeEnum, mergedValues?: readonly string[]) => void;
	}
>;

export function useLocationFilter(): Result {
	const inputRef = useRef<HTMLInputElement>(null);
	const track = useTracking();

	const {
		data: { locations, isLoading, isError, selectedLocation },
		actions: { searchDebounced, onResetQuery, selectLocation },
	} = useLocationSearch();

	const [localSearchTerm, setLocalSearchTerm] = useState<string | null>(null);
	const searchTerm = localSearchTerm ?? (selectedLocation || '');

	return {
		data: {
			data: {
				isLoading,
				isError,
				locations,
			},
			input: {
				searchTerm,
				inputRef,
			},
		},
		actions: {
			handleInputFocus: useCallback(() => {
				void track('search_location_filter_enter');
			}, [track]),
			handleInputChange: useCallback(
				(value: string) => {
					void track('search_location_filter_search');
					searchDebounced(value);
					setLocalSearchTerm(value);
				},
				[searchDebounced, track],
			),
			clearSearch: useCallback(() => {
				onResetQuery();
				setLocalSearchTerm('');
			}, [onResetQuery]),
			selectLocation,
		},
	};
}
